/**
 * useGeneratedActivity — le live retrouve l'activité que le starting block a promise (`generatedActivity`) :
 * à chaque liste relue, il demande au serveur laquelle la conversation a fait générer, et n'ouvre que
 * celle-là. Chaque ouverture se trace.
 */
import { useEffect, useRef } from "react"
import { useDispatch } from "react-redux"

import { liveService } from "../../../data/services/liveService"
import { apiCall } from "../../shared/apiCall"
import { type EngineTelemetry } from "../../shared/telemetry"
import {
    generationAskFailed,
    generationStep,
    settleGeneration,
    type GeneratedActivityAnswer,
    type GeneratedTodoCandidate,
} from "./generatedActivity"

export function useGeneratedActivity(args: {
    liveSessionId: number | null
    todos: GeneratedTodoCandidate[]
    /** L'activité générée est dans la liste : l'écran l'ouvre. */
    onFound: (activityId: number) => void
    telemetry: EngineTelemetry
}): void {
    const { liveSessionId, todos } = args
    const dispatch = useDispatch()
    const latest = useRef(args)
    latest.current = args

    useEffect(() => {
        if (liveSessionId == null) return
        const found = (activityId: number, launched = false) => {
            const what = launched ? "launched_activity" : "generated_activity"
            latest.current.telemetry.transition("generation", "opened", what, { activityId })
            latest.current.onFound(activityId)
        }
        const step = generationStep(liveSessionId, todos)
        if (step == null) return
        if ("open" in step) {
            found(step.open, step.launched)
            return
        }
        apiCall<GeneratedActivityAnswer>(dispatch as never, liveService.getGeneratedActivity, {
            routeParams: { conversation_id: step.ask },
        })
            .then((answer) => {
                const activityId = settleGeneration(liveSessionId, answer, latest.current.todos)
                if (activityId != null) found(activityId)
            })
            .catch((cause: unknown) => {
                generationAskFailed(liveSessionId)
                latest.current.telemetry.error("generated_activity", cause)
            })
    }, [liveSessionId, todos, dispatch])
}
